import React, { useState, useRef, useEffect } from 'react';
import { Languages, Loader2, ChevronDown, Check } from 'lucide-react';
import { TRANSLATION_LANGUAGES } from '../../utils/languages';

/**
 * LanguageSelector — Dropdown with flags for picking translation language.
 *
 * Props:
 *   value: current code (string) — optional
 *   onSelect: (code) => void
 *   loading: boolean
 *   label: string (button label, e.g. "Traducir")
 *   compact: boolean — show only flag/icon when true
 *   showCurrent: show current language flag in trigger
 */
const LanguageSelector = ({
  value = null,
  onSelect,
  loading = false,
  label = 'Traducir',
  compact = false,
  showCurrent = false,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const current = TRANSLATION_LANGUAGES.find(l => l.code === value);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        disabled={loading}
        className="flex items-center gap-1.5 text-xs px-2 py-1 border rounded-md bg-white hover:bg-slate-50 transition-colors h-7 disabled:opacity-50"
        data-testid="language-selector-trigger"
        title={label}
      >
        {loading ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-500" />
        ) : (
          <Languages className="h-3.5 w-3.5 text-slate-500" />
        )}
        {!compact && <span>{label}</span>}
        {showCurrent && current && <span className="text-base leading-none">{current.flag}</span>}
        <ChevronDown className="h-3 w-3 text-slate-400" />
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-1 bg-white border rounded-md shadow-lg z-30 w-56 max-h-72 overflow-y-auto"
          data-testid="language-selector-menu"
        >
          {TRANSLATION_LANGUAGES.map(lng => (
            <button
              key={lng.code}
              type="button"
              onClick={() => { onSelect(lng.code); setOpen(false); }}
              className={`w-full flex items-center gap-2.5 px-3 py-1.5 text-sm hover:bg-slate-100 text-left transition-colors ${value === lng.code ? 'bg-amber-50' : ''}`}
              data-testid={`lang-option-${lng.code}`}
            >
              <span className="text-base leading-none">{lng.flag}</span>
              <span className="flex-1 text-slate-700">{lng.native}</span>
              <span className="text-xs text-slate-400 uppercase">{lng.code}</span>
              {value === lng.code && <Check className="h-3.5 w-3.5 text-emerald-600" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default LanguageSelector;
